import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Add } from '@mui/icons-material';
import { Divider, Grid, MenuItem, Select, TextField } from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as colors from 'commons/styles/colors';
import { InventoryAdjustmentProcessState } from 'commons/types';
import {
  getActiveBankAccountsAction,
  getActivePostingPeriodsAction,
  getInventoryAdjustmentActiveLocations,
  postCompleteInventoryAdjustment,
  postInventoryAdjustmentRemoveItem,
  updateInventoryAdjustmentInfo,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setIAItemListTableError } from 'redux-stores/reducers/inventoryAdjustment.reducer';
import { AppDispatch } from 'redux-stores/store';
import { UpdateInventoryAdjustmentInfoRequest } from 'services/apis/SwipeRxWmsApiInventoryAdjustment/types';

import { InventoryAdjustmentItemForm } from './components';
import { InventoryAdjustmentStyle as S } from './styles/InventoryAdjustment.styles';

const InventoryAdjustmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const inventoryAdjustment = useSelector(
    (state: RootReducerInterface) =>
      state.inventoryAdjustment.selectedInventoryAdjustment,
  );

  if (!inventoryAdjustment) {
    navigate('/inventory-adjustment');
  }

  const { postingPeriods, bankAccounts } = useSelector(
    (state: RootReducerInterface) => state.inventoryAdjustment,
  );

  const defaultInventoryAdjustmentInfo =
    (): InventoryAdjustmentProcessState => ({
      items: [
        {
          location: 0,
          sku_code: '',
          product_info: {
            sku_code: '',
            name: '',
            uom: '',
          },
          quantity_adjustment: 0,
          details: [
            {
              bin: '',
              inventory_number: '',
              expiry_date: new Date(),
              quantity: 0,
            },
          ],
        },
      ],
      documentNumber: inventoryAdjustment?.document_number || '',
      postingPeriod: '',
      transactionDate: '',
      adjustmentAccount: '',
      memo: '',
    });

  const [inventoryAdjustmentProcessState, setInventoryAdjustmentProcessState] =
    useState<InventoryAdjustmentProcessState>(defaultInventoryAdjustmentInfo());

  const [openCompleteModal, setOpenCompleteModal] = useState<boolean>(false);
  const handleOpenCompleteDialog = () => setOpenCompleteModal(true);

  const handleCloseCompleteDialog = () => setOpenCompleteModal(false);

  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleCloseDialog = () => {
    setOpen(false);
    setItemToDelete(null);
  };

  const handleOpenDialog = (index: number) => {
    if (inventoryAdjustmentProcessState.items.length > 0) {
      setItemToDelete(index);
      setOpen(true);
    }
  };

  const removeItem = () => {
    if (itemToDelete !== null) {
      postInventoryAdjustmentRemoveItem({
        document_number: inventoryAdjustmentProcessState.documentNumber,
        item_index: itemToDelete,
      });
      setInventoryAdjustmentProcessState((prev) => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== itemToDelete),
      }));
    }
    handleCloseDialog();
  };

  const [isCompleteButtonDisabled, setCompleteButtonDisabled] = useState(false);

  useEffect(() => {
    dispatch(setIAItemListTableError([]));
    if (inventoryAdjustment) {
      setInventoryAdjustmentProcessState({
        documentNumber: inventoryAdjustment.document_number || '',
        postingPeriod: inventoryAdjustment.posting_period_name || '',
        transactionDate: inventoryAdjustment.tran_date?.toString() || '',
        adjustmentAccount: inventoryAdjustment.adjustment_account?.name || '',
        memo: inventoryAdjustment.memo || '',
        items: inventoryAdjustment.items || [],
      });
    }
  }, [inventoryAdjustment]);

  // one time fetch at the page initialization
  useEffect(() => {
    dispatch(getActivePostingPeriodsAction());
    dispatch(getActiveBankAccountsAction());
    dispatch(getInventoryAdjustmentActiveLocations());
  }, [dispatch]);

  const debouncedUpdateInfo = useDebouncedCallback(
    (params: UpdateInventoryAdjustmentInfoRequest) => {
      dispatch(
        updateInventoryAdjustmentInfo({
          documentNumber: inventoryAdjustmentProcessState.documentNumber,
          params,
        }),
      );
    },
    500,
    {
      maxWait: 2000,
    },
  );

  if (!inventoryAdjustment) {
    navigate('/inventory-adjustment');
    return <div />;
  }

  const completeInventoryAdjustment = async (): Promise<void> => {
    // TODO: validations
    dispatch(
      postCompleteInventoryAdjustment({
        documentNumber: inventoryAdjustment.document_number,
        next: () => navigate('/inventory-adjustment'),
      }),
    );
  };

  const addItem = (state: InventoryAdjustmentProcessState): void => {
    const updatedAdjusted = [
      ...state.items,
      {
        location: 0,
        sku_code: '',
        product_info: {
          sku_code: '',
          name: '',
        },
        quantity_adjustment: 0,
        details: [
          {
            bin: '',
            inventory_number: '',
            expiry_date: new Date(),
            quantity: 0,
          },
        ],
      },
    ];

    setInventoryAdjustmentProcessState({ ...state, items: updatedAdjusted });
  };

  const onInventoryAdjustmentInfoFieldChange = (
    type: keyof Pick<
      InventoryAdjustmentProcessState,
      'transactionDate' | 'postingPeriod' | 'adjustmentAccount' | 'memo'
    >,
    value: string,
  ): void => {
    const clonedState = structuredClone(inventoryAdjustmentProcessState);

    clonedState[type] = value;
    setInventoryAdjustmentProcessState(clonedState);

    const { postingPeriod, transactionDate, adjustmentAccount, memo } =
      clonedState;
    debouncedUpdateInfo({
      posting_period_name: postingPeriod,
      tran_date: new Date(transactionDate),
      adjustment_account_name: adjustmentAccount,
      memo,
    });
  };

  return (
    <div>
      <Grid container style={{ paddingTop: '8px' }}>
        <S.FieldRowWrapper item container direction="column" xs={2}>
          <Grid item>Date</Grid>
          <Grid item>
            <DateField
              variant="outlined"
              value={DateTime.fromJSDate(
                new Date(inventoryAdjustmentProcessState.transactionDate),
              )}
              onChange={(newValue) => {
                onInventoryAdjustmentInfoFieldChange(
                  'transactionDate',
                  newValue?.toString() || '',
                );
              }}
              fullWidth
            />
          </Grid>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper item xs={2}>
          Posting Period
          <Select
            value={inventoryAdjustmentProcessState.postingPeriod}
            onChange={(event) =>
              onInventoryAdjustmentInfoFieldChange(
                'postingPeriod',
                event.target.value,
              )
            }
            fullWidth
          >
            {postingPeriods.map((postingPeriod) => (
              <MenuItem value={postingPeriod.name}>
                {postingPeriod.name}
              </MenuItem>
            ))}
          </Select>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper item xs={4}>
          Adjustment Account
          <Select
            value={inventoryAdjustmentProcessState.adjustmentAccount}
            onChange={(event) =>
              onInventoryAdjustmentInfoFieldChange(
                'adjustmentAccount',
                event.target.value,
              )
            }
            fullWidth
          >
            {bankAccounts.map((bankAccount) => (
              <MenuItem value={bankAccount.name}>{bankAccount.name}</MenuItem>
            ))}
          </Select>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper item xs={4}>
          Memo
          <TextField
            variant="outlined"
            placeholder="-"
            value={inventoryAdjustmentProcessState.memo}
            onChange={(event) =>
              onInventoryAdjustmentInfoFieldChange('memo', event.target.value)
            }
            fullWidth
          />
        </S.FieldRowWrapper>
        <S.FieldRowWrapper item xs={12}>
          <Divider sx={{ borderBottomWidth: 2 }} />
        </S.FieldRowWrapper>
      </Grid>

      <InventoryAdjustmentItemForm
        processState={inventoryAdjustmentProcessState}
        setCompleteButtonDisabled={setCompleteButtonDisabled}
        setInventoryAdjustmentProcessState={setInventoryAdjustmentProcessState}
        handleOpenDialog={handleOpenDialog}
      />

      <Grid
        item
        xs={12}
        container
        style={{ textAlign: 'center', marginTop: 8 }}
      >
        <Grid item xs={0.5} />
        <S.FieldRowWrapper item xs={2}>
          <S.AddItemButton
            style={{
              fontSize: '12 !important',
              marginBottom: '8px',
              marginLeft: 0,
              width: '100% !important',
            }}
            onClick={() => addItem(inventoryAdjustmentProcessState)}
            disabled={
              inventoryAdjustmentProcessState.items.length === 0 ||
              !(
                inventoryAdjustmentProcessState.items[
                  inventoryAdjustmentProcessState.items.length - 1
                ].sku_code &&
                inventoryAdjustmentProcessState.items[
                  inventoryAdjustmentProcessState.items.length - 1
                ].quantity_adjustment &&
                inventoryAdjustmentProcessState.items[
                  inventoryAdjustmentProcessState.items.length - 1
                ].location
              )
            }
          >
            <Add /> ADD ITEM
          </S.AddItemButton>
        </S.FieldRowWrapper>
      </Grid>
      <S.StickyFooterWrapper>
        <Grid item xs={12} container>
          <Grid item xs={0.5} />
          <S.FieldRowWrapper item xs={11.5} style={{ paddingTop: 0 }}>
            <S.CompleteButton
              disabled={isCompleteButtonDisabled}
              onClick={handleOpenCompleteDialog}
            >
              COMPLETE
            </S.CompleteButton>
          </S.FieldRowWrapper>
        </Grid>
      </S.StickyFooterWrapper>
      <ConfirmationDialog
        title="Complete Inventory Adjustment Confirmation"
        content="Are you sure to complete inventory adjustment?"
        onClose={() => handleCloseCompleteDialog()}
        openModal={openCompleteModal}
        onConfirm={() => completeInventoryAdjustment()}
      />
      <ConfirmationDialog
        title="CONFIRM DELETE"
        content="Are you sure to delete this item?"
        onClose={handleCloseDialog}
        openModal={open}
        onConfirm={removeItem}
      />
    </div>
  );
};

export default InventoryAdjustmentDetailPage;
